import { create } from '@bufbuild/protobuf'
import {
  SplitSupplyNoticeDayInputSchema,
  type SplitSupplyNoticeDetailPoint,
  type SplitSupplyNoticePointInput,
  SplitSupplyNoticePointInputSchema,
  SplitSupplyNoticeValueSource,
  type SplitSupplyPartnerNoticeDetailPoint,
  type SplitSupplyPartnerNoticePointInput,
  SplitSupplyPartnerNoticePointInputSchema,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { parseSlotValue } from '~/models/splitSupplySlot'

/**
 * 自社の通告値の詳細の編集中の値 (draft)。 地点ごとに、 表示している日数 × 48 コマの文字列を
 * 日の順に並べて持つ (1 日表示は 48 個、 月全体の表示は 日数 × 48 個)。
 * 空文字 = 空欄 (保存すると空欄のまま残り、 基本パターンで埋め直されない)。
 * 値の判断 (0 以上 999,999,999 以下の整数) の正は backend で、 ここの検査は保存前に理由を出すための
 * 事前チェック (backend も同じ範囲で拒否する)。
 */
export type NoticeDraft = string[][]

/**
 * 48 コマの表 (`NoticeValueGrid`) と draft が読む、 供給地点 1 件の形。 自社の通告値の地点
 * (`SplitSupplyNoticeDetailPoint`) と他社の通告値の地点 (`SplitSupplyPartnerNoticeDetailPoint`) の
 * 両方がそのまま入る。 ベース分供給者・契約識別番号・優先順位はどちらも作成 (登録) の時に契約から写した値で、
 * 他社の通告値で写す前に作った登録の地点は持たない (表では「—」)。
 */
export type NoticeGridPoint = {
  supplyPointSpecificNumber: string
  customerName: string
  days: ReadonlyArray<{
    deliveryDate?: { year: number; month: number; day: number }
    slots: ReadonlyArray<{
      value?: bigint
      source: SplitSupplyNoticeValueSource
    }>
  }>
  base?: { businessCode: string; businessName: string }
  baseContractId?: string
  basePriority?: number
}

/** 表示している日数 (全地点で同じ)。 */
export function dayCountOf(points: readonly NoticeGridPoint[]): number {
  return points[0]?.days.length ?? 0
}

/** サーバーが返した保存済みの値から draft を作る。 */
export function draftFromPoints(
  points: readonly NoticeGridPoint[],
): NoticeDraft {
  const dayCount = dayCountOf(points)
  return points.map((p) =>
    Array.from({ length: dayCount * KOMA_COUNT }, (_, index) => {
      const value =
        p.days[Math.floor(index / KOMA_COUNT)]?.slots[index % KOMA_COUNT]?.value
      return value === undefined ? '' : String(value)
    }),
  )
}

/** draft がサーバーの表示値から変わっているか。 */
export function isDraftDirty(
  original: NoticeDraft,
  draft: NoticeDraft,
): boolean {
  return (
    original.length !== draft.length ||
    original.some((row, p) => row.some((cell, i) => cell !== draft[p]?.[i]))
  )
}

/** 値が変わった日の位置 (表示している日の何番目か)。 昇順。 */
export function changedDayIndexes(
  original: NoticeDraft,
  draft: NoticeDraft,
  dayCount: number,
): number[] {
  const changed: number[] = []
  for (let day = 0; day < dayCount; day++) {
    const from = day * KOMA_COUNT
    const differs = draft.some((row, p) =>
      row
        .slice(from, from + KOMA_COUNT)
        .some((cell, i) => cell !== original[p]?.[from + i]),
    )
    if (differs) changed.push(day)
  }
  return changed
}

export type DraftCell = { point: number; slot: number }

/** 入力が不正なコマ (空欄は不正ではない)。 slot は draft の中の位置。 */
export function findInvalidCells(draft: NoticeDraft): DraftCell[] {
  const bad: DraftCell[] = []
  for (const [point, row] of draft.entries()) {
    for (const [slot, cell] of row.entries()) {
      if (cell.trim() !== '' && parseSlotValue(cell) === undefined) {
        bad.push({ point, slot })
      }
    }
  }
  return bad
}

/** 地点の合計 (kWh)。 1 日表示は日量、 月全体の表示は月間の合計。 不正な入力・空欄は含めない。 */
export function rowTotal(row: readonly string[]): number {
  return row.reduce((sum, cell) => sum + (parseSlotValue(cell) ?? 0), 0)
}

/**
 * 1 日の 48 コマを保存の request の形にする: 値があるコマの値を コマ順に並べた `values` と、
 * 空欄のコマ番号を並べた `blankSlots` (コマごとに message を作らず、 数値の並びで送る)。
 */
export function slotsToWire(parsed: ReadonlyArray<number | undefined>): {
  values: number[]
  blankSlots: number[]
} {
  const values: number[] = []
  const blankSlots: number[] = []
  for (const [slot, value] of parsed.entries()) {
    if (value === undefined) blankSlots.push(slot)
    else values.push(value)
  }
  return { values, blankSlots }
}

/**
 * 保存の request の地点。 通の全地点を、 送る日の 48 コマ (基本パターンから入った値も含む) で送る。
 * 送る日は値が変わった日 (変わった日が無い時は表示の先頭の日)。 送らなかった日は保存済みの値のまま
 * なので、 全日を送っても結果は同じ。 楽観ロックの基準は読んだ時の更新日時。
 */
export function toSaveRequestPoints(
  points: readonly SplitSupplyNoticeDetailPoint[],
  draft: NoticeDraft,
  dayIndexes: readonly number[],
): SplitSupplyNoticePointInput[] {
  return points.map((p, index) =>
    create(SplitSupplyNoticePointInputSchema, {
      supplyPointSpecificNumber: p.supplyPointSpecificNumber,
      expectedUpdatedAt: p.updatedAt,
      days: dayIndexes.map((day) =>
        create(SplitSupplyNoticeDayInputSchema, {
          deliveryDate: p.days[day]?.deliveryDate,
          ...slotsToWire(
            Array.from({ length: KOMA_COUNT }, (_, i) =>
              parseSlotValue(draft[index]?.[day * KOMA_COUNT + i] ?? ''),
            ),
          ),
        }),
      ),
    }),
  )
}

/**
 * 他社の通告値の保存の request の地点。 登録の全地点を、 送る日の 48 コマで送る。 送る日は呼び出し側が決める
 * (他社の通告値の詳細は、 表示している全日)。 楽観ロックの基準は登録 1 件の更新日時なので地点には持たない
 * (request の `expectedUpdatedAt`)。
 */
export function toPartnerSaveRequestPoints(
  points: readonly SplitSupplyPartnerNoticeDetailPoint[],
  draft: NoticeDraft,
  dayIndexes: readonly number[],
): SplitSupplyPartnerNoticePointInput[] {
  return points.map((p, index) =>
    create(SplitSupplyPartnerNoticePointInputSchema, {
      supplyPointSpecificNumber: p.supplyPointSpecificNumber,
      days: dayIndexes.map((day) =>
        create(SplitSupplyNoticeDayInputSchema, {
          deliveryDate: p.days[day]?.deliveryDate,
          ...slotsToWire(
            Array.from({ length: KOMA_COUNT }, (_, i) =>
              parseSlotValue(draft[index]?.[day * KOMA_COUNT + i] ?? ''),
            ),
          ),
        }),
      ),
    }),
  )
}

/** コマの値の出所のうち、 空欄 (値が入っていないコマ) を色で示す対象か。 */
export const isBlankSource = (
  source: SplitSupplyNoticeValueSource | undefined,
): boolean => source === SplitSupplyNoticeValueSource.BLANK
